import { $toggleLink } from '@lexical/link';
import { $findMatchingParent } from '@lexical/utils';
import {
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  LexicalNode,
  PointType,
  RangeSelection,
  TextNode,
} from 'lexical';
import * as Constants from 'const';
import { $unwrapNodes, $wrapNodes, $isNoWrapTextNode } from '../../nodes/NoWrapNode';
import { BaseProps } from './types';

function $getSelectionTextNodes(selection: RangeSelection): TextNode[] {
  const selectedTextNodes = new Map<string, TextNode>();

  selection.getNodes().forEach((node) => {
    if ($isTextNode(node)) {
      selectedTextNodes.set(node.getKey(), node);

      return;
    }
    if ($isElementNode(node)) {
      node.getAllTextNodes().forEach((item) => {
        selectedTextNodes.set(item.getKey(), item);
      });
    }
  });

  return Array.from(selectedTextNodes.values());
}

function $getScriptStyleFromSelection(): BaseProps['scriptStyle'] {
  const selection = $getSelection();

  if (!$isRangeSelection(selection)) {
    return undefined;
  }

  if (selection.hasFormat('superscript')) {
    return Constants.ScriptType.SUPERSCRIPT;
  }

  if (selection.hasFormat('subscript')) {
    return Constants.ScriptType.SUBSCRIPT;
  }

  return undefined;
}

export function $getBaseProps(): BaseProps {
  return {
    scriptStyle: $getScriptStyleFromSelection(),
  };
}

function $clearTextNodeFormatting(
  node: TextNode,
  idx: number,
  nodes: LexicalNode[],
  extractedNodes: LexicalNode[],
  anchor: PointType,
  focus: PointType,
): void {
  let textNode = node;

  if (idx === 0 && anchor.offset !== 0) {
    textNode = textNode.splitText(anchor.offset)[1] || textNode;
  }
  if (idx === nodes.length - 1) {
    textNode = textNode.splitText(focus.offset)[0] || textNode;
  }
  if (nodes.length === 1 && $isTextNode(extractedNodes[0])) {
    textNode = extractedNodes[0];
  }
  if (textNode.getStyle() !== '') {
    textNode.setStyle('');
  }
  if (textNode.getFormat() !== 0) {
    textNode.setFormat(0);
    const blockNode = $findMatchingParent(textNode, curNode => $isElementNode(curNode) && !curNode.isInline());
    if (!$isElementNode(blockNode)) {
      return;
    }
    blockNode.setFormat('');
  }
}

export function $clearNodesFormatting(): void {
  const selection = $getSelection();
  if (!$isRangeSelection(selection)) {
    return;
  }
  $toggleLink(null);
  const anchor = selection.anchor;
  const focus = selection.focus;
  if (anchor.key === focus.key && anchor.offset === focus.offset) {
    return;
  }
  const nodes = selection.getNodes();
  const extractedNodes = selection.extract();
  nodes.forEach((node, idx) => {
    if (!$isTextNode(node)) {
      return;
    }
    $clearTextNodeFormatting(node, idx, nodes, extractedNodes, anchor, focus);
  });
}

export function $toggleNoWrap(selection: RangeSelection): void {
  const selectedTextNodes = $getSelectionTextNodes(selection);
  const allSelectedNoWrap = selectedTextNodes.length > 0 && selectedTextNodes.every($isNoWrapTextNode);

  // Toggle OFF for both modern NoWrapNode and legacy inline styles.
  // $unwrapNodes handles NoWrapNode removal and legacy style cleanup with merge.
  if (allSelectedNoWrap) {
    $unwrapNodes(selection);

    return;
  }

  // Otherwise create modern NoWrapNode wrapper (toggle ON).
  $wrapNodes(selection);
}
